import { Tab, TabList, makeStyles, tokens } from '@fluentui/react-components';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SHRINKABLE } from '../responsive';

import { BonusNamesTab } from './BonusNamesTab';
import { CoSupervisionTab } from './CoSupervisionTab';
import { DepartmentMappingTab } from './DepartmentMappingTab';
import { DiagnosticsTab } from './DiagnosticsTab';
import { EmployeesTab } from './EmployeesTab';
import { ExceptionsTab } from './ExceptionsTab';
import { KiosksTab } from './KiosksTab';
import { NsConnectionTab } from './NsConnectionTab';
import { ReportsTab } from './ReportsTab';

/**
 * The Admin hub's sub-surfaces. `employees` is the landing section — the roster is
 * what an admin opens this hub for far more often than the connection config.
 *
 * NOTE there is no `employee-mapping` section any more: the Employees roster
 * ABSORBED it (the same rows, plus payroll item / departments / split / PIN), and
 * every surface unique to it was carried over rather than dropped — the org-chart
 * badges and the NetSuite sync onto the roster itself, the Entra login links into
 * {@link import('./EntraLinksSection').EntraLinksSection}.
 */
const ADMIN_SECTIONS = [
  'employees',
  'salary',
  'exceptions',
  'coSupervision',
  'netsuite',
  'kiosks',
  'departments',
  'reports',
  'bonusNames',
  'diagnostics',
] as const;

type AdminSection = (typeof ADMIN_SECTIONS)[number];

const DEFAULT_SECTION: AdminSection = 'employees';

/** Narrow a raw URL segment to a known section, falling back to the default. */
function toSection(raw: string | undefined): AdminSection {
  return ADMIN_SECTIONS.includes(raw as AdminSection) ? (raw as AdminSection) : DEFAULT_SECTION;
}

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    // Every ancestor between the page and a table's scroller must be able to
    // shrink, or the overflow lands on the document instead. See responsive.ts.
    ...SHRINKABLE,
  },
  subnav: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    ...SHRINKABLE,
  },
});

/**
 * Admin hub (Administrator-only). Consolidates every configuration surface —
 * Employees, Salary, NetSuite connection, Kiosks, Departments and Diagnostics —
 * into one tab with an internal sub-nav. Gated to Admins by both the nav (hidden
 * otherwise) and the API (every underlying endpoint is Admin-gated server-side).
 *
 * The active section lives in the URL (`/admin/:section`) rather than in component
 * state, so a section is linkable and survives a refresh — and so the retired
 * top-level `/employees` and `/salary` routes can redirect straight to their new
 * home instead of dropping an old bookmark on the wrong page.
 */
export function AdminTab(): JSX.Element {
  const styles = useStyles();
  const navigate = useNavigate();
  const { section: rawSection } = useParams<{ section?: string }>();
  const section = toSection(rawSection);

  const select = useCallback(
    (value: string) => {
      // `navigate` is typed as possibly-thenable; nothing here awaits the
      // transition, so the result is explicitly discarded.
      void navigate(`/admin/${toSection(value)}`);
    },
    [navigate]
  );

  return (
    <div className={styles.page}>
      {/*
        The sub-nav is TEN tabs — far wider than a phone. A wide child escapes
        its parent's box entirely (maxWidth:100% caps the PARENT, it does not clip
        the CHILD), so without its own scroller this strip stretched the whole
        document sideways and dragged the header and table off-screen with it
        (Michael, 2026-09-10). Same treatment as the top-level nav in TenantShell:
        swipe the strip, don't move the page.
      */}
      <div className={styles.subnav}>
        <TabList selectedValue={section} onTabSelect={(_, d) => select(d.value as string)}>
        <Tab value="employees">Employees</Tab>
        <Tab value="salary">Salary</Tab>
        <Tab value="exceptions">Exceptions</Tab>
        <Tab value="coSupervision">Co-supervision</Tab>
        <Tab value="netsuite">NetSuite</Tab>
        <Tab value="kiosks">Kiosks</Tab>
        <Tab value="departments">Departments</Tab>
        <Tab value="reports">Reports</Tab>
        <Tab value="bonusNames">Bonus names</Tab>
        <Tab value="diagnostics">Diagnostics</Tab>
        </TabList>
      </div>
      {/*
        Keyed so switching between the two roster scopes REMOUNTS the component.
        Both sections are the same `EmployeesTab` with a different scope, and its
        state (blades, filters, fetched rows) is per-scope — without the key React
        would reuse the mounted instance and carry Salary state into Employees.
      */}
      {section === 'employees' ? <EmployeesTab key="clockable" scope="clockable" /> : null}
      {section === 'salary' ? <EmployeesTab key="salary" scope="salary" /> : null}
      {section === 'exceptions' ? <ExceptionsTab /> : null}
      {section === 'coSupervision' ? <CoSupervisionTab /> : null}
      {section === 'netsuite' ? <NsConnectionTab /> : null}
      {section === 'kiosks' ? <KiosksTab /> : null}
      {section === 'departments' ? <DepartmentMappingTab /> : null}
      {section === 'reports' ? <ReportsTab /> : null}
      {section === 'bonusNames' ? <BonusNamesTab /> : null}
      {section === 'diagnostics' ? <DiagnosticsTab /> : null}
    </div>
  );
}
